import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Loader2 } from 'lucide-react';
import { ANALYTICS_EVENT_NAMES, ANALYTICS_SURFACES } from '@bloom/shared';
import apiClient from '../lib/api';
import { AsyncSearchSelect } from '../components/common/AsyncSearchSelect';
import type { User } from '@bloom/shared';
import { useTheme } from '../hooks/useTheme';

/**
 * Patient analytics (storefront, patient sign-in, portal) over the
 * first-party event store: GET /api/admin/analytics/* for admins, and
 * GET /api/analytics/clinic/overview for a clinic's own patients
 * ("Patient insights"). One filter row scopes every section.
 *
 * Chart rules (dataviz skill): one axis per chart, single-series bars in
 * series slot 1, solid hairline grid, 2px lines, a legend whenever two
 * series share a plot, and a table for every chart's values.
 */

type Row = Record<string, any>;
interface Overview {
  totals: { events: number; sessions: number; visitors: number; identifiedPatients: number; activePatients: number; paidOrders: number; revenue: number };
  daily: { date: string; sessions: number; patients: number; orders: number; paidOrders: number; revenue: number }[];
  acquisition: { byChannel: Row[]; bySource: Row[]; byCampaign: Row[] };
  landingPages: Row[];
  referrers: Row[];
  devices: { types: Row[]; os: Row[]; browsers: Row[]; countries: Row[] };
  features: { name: string; description: string; patients: number; events: number; pctOfActive: number }[];
  funnel: { overall: { step: string; sessions: number }[]; byChannel: { channel: string | null; steps: { step: string; sessions: number }[] }[] };
}

const RANGES = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'Last 12 months', days: 365 },
];

const STEP_LABELS: Record<string, string> = {
  session: 'Sessions', product_viewed: 'Viewed a product', checkout_started: 'Started checkout',
  signup_completed: 'Signed up', order_created: 'Placed an order', order_paid: 'Paid',
};
const CHANNEL_LABELS: Record<string, string> = {
  paid_search: 'Paid search', paid_social: 'Paid social', organic_search: 'Organic search', organic_social: 'Organic social',
  email: 'Email', sms: 'Text message', referral: 'Referral', direct: 'Direct', qr: 'QR / print', clinic_link: 'Clinic link', other: 'Other',
};

/** Validated categorical slots 1-2 (light / dark steps of the reference palette). */
const SERIES = { light: ['#2a78d6', '#eb6834'], dark: ['#3987e5', '#d95926'] };

const compact = (n: number) => new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n || 0);
const whole = (n: number) => new Intl.NumberFormat('en-US').format(n || 0);
const money = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
const pct = (n: number) => `${(Math.round((n || 0) * 1000) / 10).toFixed(1)}%`;
const shortDate = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

function useRangeParams(days: number, physicianId: string, surface: string) {
  return useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - days * 864e5);
    const params = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
    if (physicianId) params.set('physicianId', physicianId);
    if (surface) params.set('surface', surface);
    return params;
    // Rounded to the day so the query key is stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, physicianId, surface, new Date().toDateString()]);
}

export function PatientAnalytics() {
  return <PatientAnalyticsDashboard scope="admin" />;
}

export function PatientInsights() {
  return <PatientAnalyticsDashboard scope="clinic" />;
}

const clinicLabel = (u: User) =>
  u.profile?.clinicName || [u.profile?.firstName, u.profile?.lastName].filter(Boolean).join(' ') || u.email;

function PatientAnalyticsDashboard({ scope }: { scope: 'admin' | 'clinic' }) {
  const [days, setDays] = useState(30);
  // Clinic filter: a server-searched picker. There are more clinics than one
  // page of /users/physicians holds, so a <select> filled by one fetch dropped
  // everyone past the cap.
  const [clinic, setClinic] = useState<User | null>(null);
  const physicianId = clinic ? String(clinic._id) : '';
  const [surface, setSurface] = useState('');
  const params = useRangeParams(days, scope === 'admin' ? physicianId : '', surface);
  const url = scope === 'admin' ? '/admin/analytics/overview' : '/analytics/clinic/overview';

  const overview = useQuery<Overview>({
    queryKey: ['patient-analytics', scope, params.toString()],
    queryFn: async () => (await apiClient.get<{ data: Overview }>(`${url}?${params}`)).data,
    placeholderData: previous => previous,
  });

  const data = overview.data;
  const select = 'h-9 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 dark:border-white/10 dark:bg-zinc-900 dark:text-zinc-100';

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6">
      <header>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-zinc-100">{scope === 'admin' ? 'Patient analytics' : 'Patient insights'}</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-zinc-400">
          {scope === 'admin'
            ? 'How patients find the storefronts, sign in and use the portal, across every clinic.'
            : 'How your patients find your store, check out and use their portal.'}
        </p>
      </header>

      {/* One filter row scopes every section below. */}
      <div className="flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="pa-range">Date range</label>
        <select id="pa-range" className={select} value={days} onChange={e => setDays(Number(e.target.value))}>
          {RANGES.map(r => <option key={r.days} value={r.days}>{r.label}</option>)}
        </select>
        {scope === 'admin' && <>
          <div className="w-64">
            <AsyncSearchSelect<User>
              fetchPage={({ page, pageSize, search, signal }) => apiClient.searchUsersPage({ role: 'physician', page, pageSize, search, signal })}
              getKey={u => String(u._id)}
              getLabel={clinicLabel}
              value={clinic}
              onChange={setClinic}
              placeholder="All clinics"
              noun="clinics"
              aria-label="Clinic"
            />
          </div>
        </>}
        <label className="sr-only" htmlFor="pa-surface">Surface</label>
        <select id="pa-surface" className={select} value={surface} onChange={e => setSurface(e.target.value)}>
          <option value="">All surfaces</option>
          {ANALYTICS_SURFACES.filter(s => s !== 'server').map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        {overview.isFetching && <Loader2 aria-label="Updating" className="h-4 w-4 animate-spin text-gray-400" />}
      </div>

      {overview.isError && !data ? <p role="alert" className="rounded-xl bg-red-50 p-4 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-200">Analytics couldn’t be loaded. <button className="underline" onClick={() => overview.refetch()}>Try again</button></p>
        : !data ? <p role="status" className="py-16 text-center text-sm text-gray-500">Loading analytics…</p>
        : <div className={`space-y-6 transition-opacity ${overview.isFetching ? 'opacity-60' : ''}`}>
          <section aria-label="Totals" className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            <StatTile label="Sessions" value={compact(data.totals.sessions)} />
            <StatTile label="Visitors" value={compact(data.totals.visitors)} />
            <StatTile label="Identified patients" value={compact(data.totals.identifiedPatients)} />
            <StatTile label="Active portal patients" value={compact(data.totals.activePatients)} />
            <StatTile label="Paid orders" value={compact(data.totals.paidOrders)} />
            <StatTile label="Paid order revenue" value={money(data.totals.revenue)} />
          </section>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card title="Sessions and patients" subtitle="Per day. Patients: identified patients active that day.">
              <TimeSeries data={data.daily} series={[{ key: 'sessions', name: 'Sessions' }, { key: 'patients', name: 'Patients' }]} />
            </Card>
            <Card title="Orders" subtitle="Per day: orders created and orders paid.">
              <TimeSeries data={data.daily} series={[{ key: 'orders', name: 'Orders created' }, { key: 'paidOrders', name: 'Orders paid' }]} />
            </Card>
          </div>

          <Card title="Acquisition by channel" subtitle="Each session’s channel is how that visit arrived; outcomes are the orders and payments it led to.">
            <AcquisitionTable rows={data.acquisition.byChannel} keyName="channel" label="Channel" format={v => CHANNEL_LABELS[v] || v || 'Unknown'} />
          </Card>
          <div className="grid gap-6 lg:grid-cols-2">
            <Card title="By campaign source" subtitle="utm_source"><AcquisitionTable rows={data.acquisition.bySource} keyName="source" label="Source" compactCols /></Card>
            <Card title="By campaign" subtitle="utm_campaign"><AcquisitionTable rows={data.acquisition.byCampaign} keyName="campaign" label="Campaign" compactCols /></Card>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card title="Funnel" subtitle="Sessions reaching each step (a later step counts the earlier ones as reached).">
              <RankedBars rows={data.funnel.overall.map((s: { step: string; sessions: number }) => ({ label: STEP_LABELS[s.step] || s.step, value: s.sessions }))}
                note={(row, i, all) => (i === 0 || !all[0].value ? '' : pct(row.value / all[0].value))} keepOrder />
            </Card>
            <Card title="Funnel by channel" subtitle="Share of each channel’s sessions reaching the step.">
              <FunnelByChannel rows={data.funnel.byChannel} />
            </Card>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            <Card title="Device type"><RankedBars rows={toBars(data.devices.types, 'type')} /></Card>
            <Card title="Operating system"><RankedBars rows={toBars(data.devices.os, 'os')} /></Card>
            <Card title="Browser"><RankedBars rows={toBars(data.devices.browsers, 'browser')} /></Card>
            <Card title="Country"><RankedBars rows={toBars(data.devices.countries, 'country')} /></Card>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card title="Landing pages" subtitle="First page of each session (path only)."><RankedBars rows={toBars(data.landingPages, 'path')} mono /></Card>
            <Card title="Referring sites" subtitle="Where sessions came from, by domain."><RankedBars rows={toBars(data.referrers, 'domain')} /></Card>
          </div>

          <Card title="Portal feature adoption" subtitle={`Patients who used each feature, as a share of ${whole(data.totals.activePatients)} active portal patients.`}>
            <FeatureTable rows={data.features} />
          </Card>

          {scope === 'admin' && <EventExplorer physicianId={physicianId} days={days} />}
        </div>}
    </div>
  );
}

function toBars(rows: Row[], key: string) {
  return rows.map(r => ({ label: r[key] ?? 'Unknown', value: r.sessions }));
}

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return <section className="rounded-xl border border-gray-200 bg-white p-5 dark:border-white/10 dark:bg-zinc-900">
    <h2 className="text-base font-semibold text-gray-900 dark:text-zinc-100">{title}</h2>
    {subtitle && <p className="mt-0.5 text-sm text-gray-500 dark:text-zinc-400">{subtitle}</p>}
    <div className="mt-4">{children}</div>
  </section>;
}

function StatTile({ label, value }: { label: string; value: string }) {
  return <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-zinc-900">
    <p className="text-sm text-gray-600 dark:text-zinc-400">{label}</p>
    <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-zinc-100">{value}</p>
  </div>;
}

/** Two series at most, on one shared axis (the pairs plotted together share a unit). */
function TimeSeries({ data, series }: { data: Overview['daily']; series: { key: string; name: string }[] }) {
  const { isDark } = useTheme();
  const colors = isDark ? SERIES.dark : SERIES.light;
  const surface = isDark ? '#18181b' : '#ffffff';
  const grid = isDark ? 'rgba(255,255,255,0.08)' : '#ececea';
  const tick = isDark ? '#a1a1aa' : '#6b7280';
  return <>
    <div className="h-[260px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
          <CartesianGrid stroke={grid} vertical={false} />
          <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 12, fill: tick }} stroke={grid} minTickGap={24} />
          <YAxis tickFormatter={compact} tick={{ fontSize: 12, fill: tick }} stroke={grid} allowDecimals={false} width={40} />
          <Tooltip cursor={{ stroke: tick, strokeWidth: 1 }} labelFormatter={v => shortDate(String(v))} formatter={(v: any, name: any) => [whole(Number(v)), name]}
            contentStyle={{ background: surface, border: `1px solid ${grid}`, borderRadius: 8, color: isDark ? '#f4f4f5' : '#111827' }} />
          {series.length > 1 && <Legend iconType="plainline" wrapperStyle={{ fontSize: 12, color: tick }} />}
          {series.map((s, i) => <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={colors[i]} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
            dot={false} activeDot={{ r: 4, fill: colors[i], stroke: surface, strokeWidth: 2 }} isAnimationActive={false} />)}
        </LineChart>
      </ResponsiveContainer>
    </div>
    <details className="mt-2 text-sm">
      <summary className="cursor-pointer text-gray-500 dark:text-zinc-400">Show table</summary>
      <div className="mt-2 max-h-60 overflow-auto">
        <table className="w-full text-left text-sm tabular-nums">
          <thead className="text-gray-500 dark:text-zinc-400"><tr><th className="py-1 font-medium">Date</th>{series.map(s => <th key={s.key} className="py-1 text-right font-medium">{s.name}</th>)}</tr></thead>
          <tbody className="text-gray-800 dark:text-zinc-200">{data.map(d => <tr key={d.date}><td className="py-0.5">{shortDate(d.date)}</td>{series.map(s => <td key={s.key} className="py-0.5 text-right">{whole((d as any)[s.key])}</td>)}</tr>)}</tbody>
        </table>
      </div>
    </details>
  </>;
}

/**
 * A ranked list with a thin bar per row: single series, so every bar is
 * slot 1 and the value is written beside it (the list is its own table).
 */
function RankedBars({ rows, note, keepOrder = false, mono = false }: {
  rows: { label: string; value: number }[];
  note?: (row: { label: string; value: number }, index: number, all: { label: string; value: number }[]) => string;
  keepOrder?: boolean;
  mono?: boolean;
}) {
  const { isDark } = useTheme();
  const list = keepOrder ? rows : [...rows].sort((a, b) => b.value - a.value);
  const max = Math.max(1, ...list.map(r => r.value));
  if (!list.length || list.every(r => !r.value)) return <p className="text-sm text-gray-500 dark:text-zinc-400">No data in this range.</p>;
  return <ul className="space-y-2.5">
    {list.map((row, i) => <li key={`${row.label}-${i}`} title={`${row.label}: ${whole(row.value)}`}>
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className={`min-w-0 truncate text-gray-800 dark:text-zinc-200 ${mono ? 'font-mono text-xs' : ''}`}>{row.label}</span>
        <span className="shrink-0 tabular-nums text-gray-600 dark:text-zinc-400">{whole(row.value)}{note?.(row, i, list) ? <span className="ml-2 text-gray-500">{note(row, i, list)}</span> : null}</span>
      </div>
      <div className="mt-1 h-2">
        <div className="h-2 rounded-r" style={{ width: `${Math.max(row.value ? 1 : 0, (row.value / max) * 100)}%`, background: isDark ? SERIES.dark[0] : SERIES.light[0] }} />
      </div>
    </li>)}
  </ul>;
}

function AcquisitionTable({ rows, keyName, label, format = v => v ?? '(none)', compactCols = false }: {
  rows: Row[]; keyName: string; label: string; format?: (v: any) => string; compactCols?: boolean;
}) {
  if (!rows.length) return <p className="text-sm text-gray-500 dark:text-zinc-400">No sessions in this range.</p>;
  const th = 'py-2 pr-3 text-right font-medium';
  return <div className="overflow-x-auto">
    <table className="w-full min-w-[32rem] text-sm tabular-nums">
      <thead className="border-b border-gray-200 text-gray-500 dark:border-white/10 dark:text-zinc-400">
        <tr><th className="py-2 pr-3 text-left font-medium">{label}</th><th className={th}>Sessions</th><th className={th}>Sign-ups</th>{!compactCols && <th className={th}>Orders</th>}<th className={th}>Paid</th><th className={th}>Revenue</th><th className={th}>Conversion</th></tr>
      </thead>
      <tbody className="text-gray-800 dark:text-zinc-200">
        {rows.map((r, i) => <tr key={`${r[keyName]}-${i}`} className="border-b border-gray-100 last:border-0 dark:border-white/5">
          <td className="py-2 pr-3 text-left">{format(r[keyName])}</td>
          <td className="py-2 pr-3 text-right">{whole(r.sessions)}</td>
          <td className="py-2 pr-3 text-right">{whole(r.signups)}</td>
          {!compactCols && <td className="py-2 pr-3 text-right">{whole(r.orders)}</td>}
          <td className="py-2 pr-3 text-right">{whole(r.paidOrders)}</td>
          <td className="py-2 pr-3 text-right">{money(r.revenue)}</td>
          <td className="py-2 pr-3 text-right">{pct(r.conversionRate)}</td>
        </tr>)}
      </tbody>
    </table>
  </div>;
}

function FunnelByChannel({ rows }: { rows: Overview['funnel']['byChannel'] }) {
  if (!rows.length) return <p className="text-sm text-gray-500 dark:text-zinc-400">No sessions in this range.</p>;
  const steps = rows[0].steps.map(s => s.step);
  return <div className="overflow-x-auto">
    <table className="w-full min-w-[30rem] text-sm tabular-nums">
      <thead className="border-b border-gray-200 text-gray-500 dark:border-white/10 dark:text-zinc-400">
        <tr><th className="py-2 pr-3 text-left font-medium">Channel</th>{steps.map(s => <th key={s} className="py-2 pr-3 text-right font-medium">{STEP_LABELS[s] || s}</th>)}</tr>
      </thead>
      <tbody className="text-gray-800 dark:text-zinc-200">
        {rows.map(r => {
          const base = r.steps[0]?.sessions || 0;
          return <tr key={r.channel ?? 'unknown'} className="border-b border-gray-100 last:border-0 dark:border-white/5">
            <td className="py-2 pr-3">{CHANNEL_LABELS[r.channel ?? ''] || r.channel || 'Unknown'}</td>
            {r.steps.map((s, i) => <td key={s.step} className="py-2 pr-3 text-right">{i === 0 ? whole(s.sessions) : base ? pct(s.sessions / base) : '–'}</td>)}
          </tr>;
        })}
      </tbody>
    </table>
  </div>;
}

function FeatureTable({ rows }: { rows: Overview['features'] }) {
  const { isDark } = useTheme();
  return <div className="overflow-x-auto">
    <table className="w-full min-w-[36rem] text-sm">
      <thead className="border-b border-gray-200 text-gray-500 dark:border-white/10 dark:text-zinc-400">
        <tr><th className="py-2 pr-3 text-left font-medium">Feature</th><th className="py-2 pr-3 text-right font-medium">Patients</th><th className="py-2 pr-3 text-right font-medium">Uses</th><th className="w-1/3 py-2 pr-3 text-left font-medium">Share of active patients</th></tr>
      </thead>
      <tbody className="text-gray-800 dark:text-zinc-200">
        {rows.map(f => <tr key={f.name} className="border-b border-gray-100 last:border-0 dark:border-white/5" title={f.description}>
          <td className="py-2 pr-3"><span className="font-mono text-xs">{f.name}</span><span className="block text-xs text-gray-500 dark:text-zinc-400">{f.description}</span></td>
          <td className="py-2 pr-3 text-right tabular-nums">{whole(f.patients)}</td>
          <td className="py-2 pr-3 text-right tabular-nums">{whole(f.events)}</td>
          <td className="py-2 pr-3">
            <div className="flex items-center gap-2">
              <div className="h-2 flex-1"><div className="h-2 rounded-r" style={{ width: `${Math.min(100, f.pctOfActive * 100)}%`, background: isDark ? SERIES.dark[0] : SERIES.light[0] }} /></div>
              <span className="w-14 shrink-0 text-right tabular-nums text-gray-600 dark:text-zinc-400">{pct(f.pctOfActive)}</span>
            </div>
          </td>
        </tr>)}
      </tbody>
    </table>
  </div>;
}

interface ExplorerEvent {
  _id: string; name: string; source: string; surface: string; occurredAt: string; sessionId?: string; anonymousId?: string;
  patientId?: string; physicianId?: string; path?: string; properties?: Record<string, unknown>;
  context?: { device?: { type?: string; os?: string; browser?: string }; attribution?: { channel?: string }; geo?: { country?: string } };
}

/** Raw events, newest first, with "Load more" on the server's cursor. */
function EventExplorer({ physicianId, days }: { physicianId: string; days: number }) {
  const [name, setName] = useState('');
  const [patientId, setPatientId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [pages, setPages] = useState<string[]>(['']);
  const params = useRangeParams(days, physicianId, '');
  const filters = new URLSearchParams(params);
  if (name) filters.set('name', name);
  if (/^[a-f0-9]{24}$/i.test(patientId.trim())) filters.set('patientId', patientId.trim());
  if (/^[A-Za-z0-9_-]{8,64}$/.test(sessionId.trim())) filters.set('sessionId', sessionId.trim());
  filters.set('limit', '50');
  const key = filters.toString();

  const results = useQuery({
    queryKey: ['patient-analytics-events', key, pages],
    queryFn: async () => {
      const all: ExplorerEvent[] = [];
      let next: string | null = null;
      for (const cursor of pages) {
        const q = new URLSearchParams(filters);
        if (cursor) q.set('cursor', cursor);
        const res = await apiClient.get<{ data: { events: ExplorerEvent[]; nextCursor: string | null } }>(`/admin/analytics/events?${q}`);
        all.push(...res.data.events);
        next = res.data.nextCursor;
      }
      return { events: all, next };
    },
    placeholderData: previous => previous,
  });
  const reset = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPages(['']); };
  const input = 'h-9 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 dark:border-white/10 dark:bg-zinc-900 dark:text-zinc-100';

  return <Card title="Event explorer" subtitle="Raw events in the selected range, newest first. Properties are ids, enums and counts only.">
    <div className="mb-3 flex flex-wrap gap-2">
      <label className="sr-only" htmlFor="pa-event-name">Event</label>
      <select id="pa-event-name" className={input} value={name} onChange={e => reset(setName)(e.target.value)}>
        <option value="">All events</option>
        {[...ANALYTICS_EVENT_NAMES].sort().map(n => <option key={n} value={n}>{n}</option>)}
      </select>
      <input aria-label="Patient id" placeholder="Patient id" className={`${input} w-56 font-mono`} value={patientId} onChange={e => reset(setPatientId)(e.target.value)} />
      <input aria-label="Session id" placeholder="Session id" className={`${input} w-56 font-mono`} value={sessionId} onChange={e => reset(setSessionId)(e.target.value)} />
    </div>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[56rem] text-left text-sm">
        <thead className="border-b border-gray-200 text-gray-500 dark:border-white/10 dark:text-zinc-400">
          <tr><th className="py-2 pr-3 font-medium">Time</th><th className="py-2 pr-3 font-medium">Event</th><th className="py-2 pr-3 font-medium">Surface</th><th className="py-2 pr-3 font-medium">Path</th><th className="py-2 pr-3 font-medium">Properties</th><th className="py-2 pr-3 font-medium">Patient</th><th className="py-2 pr-3 font-medium">Session</th><th className="py-2 pr-3 font-medium">Device</th></tr>
        </thead>
        <tbody className="text-gray-800 dark:text-zinc-200">
          {(results.data?.events || []).map((e: ExplorerEvent) => <tr key={e._id} className="border-b border-gray-100 align-top last:border-0 dark:border-white/5">
            <td className="whitespace-nowrap py-1.5 pr-3 tabular-nums">{new Date(e.occurredAt).toLocaleString()}</td>
            <td className="py-1.5 pr-3 font-mono text-xs">{e.name}<span className="ml-1 text-gray-400">{e.source === 'server' ? '· server' : ''}</span></td>
            <td className="py-1.5 pr-3">{e.surface}</td>
            <td className="max-w-[14rem] truncate py-1.5 pr-3 font-mono text-xs" title={e.path}>{e.path || '–'}</td>
            <td className="max-w-[16rem] py-1.5 pr-3 font-mono text-xs">{e.properties && Object.keys(e.properties).length ? Object.entries(e.properties).map(([k, v]) => `${k}=${String(v)}`).join(' ') : '–'}</td>
            <td className="py-1.5 pr-3 font-mono text-xs">{e.patientId ? <button className="underline" onClick={() => reset(setPatientId)(String(e.patientId))}>{String(e.patientId).slice(-6)}</button> : '–'}</td>
            <td className="py-1.5 pr-3 font-mono text-xs">{e.sessionId ? <button className="underline" onClick={() => reset(setSessionId)(e.sessionId!)}>{e.sessionId.slice(-8)}</button> : '–'}</td>
            <td className="py-1.5 pr-3 text-xs">{[e.context?.device?.type, e.context?.device?.browser, e.context?.geo?.country].filter(Boolean).join(' · ') || '–'}</td>
          </tr>)}
        </tbody>
      </table>
    </div>
    {results.isPending && <p role="status" className="py-6 text-center text-sm text-gray-500">Loading events…</p>}
    {results.data && !results.data.events.length && <p className="py-6 text-center text-sm text-gray-500">No events match.</p>}
    {results.data?.next && <button className="mt-3 h-9 rounded-lg border border-gray-300 px-4 text-sm dark:border-white/10" disabled={results.isFetching}
      onClick={() => setPages(p => [...p, results.data!.next!])}>Load more</button>}
  </Card>;
}

export default PatientAnalytics;
